import {useNavigate} from 'react-router-dom'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {useServices} from '@/services/context'

export interface UseValueListMutationsOptions {
    onAddEntrySuccess?: () => void
    onUpdateEntrySuccess?: () => void
    onDeleteEntrySuccess?: () => void
    onUpdateListSuccess?: () => void
}

export interface AddEntryVariables {
    code: string
    label: string
    description: string | null
}

export interface UpdateEntryVariables {
    entryId: string
    label: string
    description: string | null
    active: boolean
    version: number
}

export interface UpdateListVariables {
    displayName: string
    description: string | null
    active: boolean
    version: number
}

/**
 * Composite mutations hook for the ValueListDetail page. Bundles every write
 * operation against `services.valueLists.*` so the page body never has to
 * derive query-invalidation strings or success-toast keys inline. Each
 * returned member is a stock TanStack `useMutation` result.
 *
 * The page (and 603.05 / 603.08 — see issue 603.02) reuse this scaffolding
 * shape: composite hook + per-mutation `onSuccess` callbacks routed back to
 * page state.
 */
export function useValueListMutations(
    listId: string | undefined,
    options: UseValueListMutationsOptions = {},
) {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {onAddEntrySuccess, onUpdateEntrySuccess, onDeleteEntrySuccess, onUpdateListSuccess} = options

    const addEntry = useEntityMutation({
        mutationFn: (data: AddEntryVariables) =>
            services.valueLists.addEntry(listId!, data),
        invalidate: ['value-list', listId],
        successMessage: i18n.valueLists.messages.addEntrySuccess,
        onSuccess: () => {
            onAddEntrySuccess?.()
        },
    })

    const updateEntry = useEntityMutation({
        mutationFn: (data: UpdateEntryVariables) =>
            services.valueLists.updateEntry(listId!, data.entryId, {
                label: data.label,
                description: data.description,
                active: data.active,
                version: data.version,
            }),
        invalidate: ['value-list', listId],
        successMessage: i18n.valueLists.messages.updateEntrySuccess,
        onSuccess: () => {
            onUpdateEntrySuccess?.()
        },
    })

    const deleteEntry = useEntityMutation({
        mutationFn: (entryId: string) =>
            services.valueLists.removeEntry(listId!, entryId),
        invalidate: ['value-list', listId],
        successMessage: i18n.valueLists.messages.deleteEntrySuccess,
        onSuccess: () => {
            onDeleteEntrySuccess?.()
        },
    })

    const updateList = useEntityMutation({
        mutationFn: (data: UpdateListVariables) =>
            services.valueLists.update(listId!, data),
        invalidate: [['value-list', listId], ['value-lists']],
        successMessage: i18n.valueLists.messages.updateSuccess,
        onSuccess: () => {
            onUpdateListSuccess?.()
        },
    })

    const deleteList = useEntityMutation({
        mutationFn: () => services.valueLists.remove(listId!),
        invalidate: ['value-lists'],
        successMessage: i18n.valueLists.messages.deleteSuccess,
        onSuccess: () => {
            navigate(routes.schema.valueLists.list())
        },
    })

    return {addEntry, updateEntry, deleteEntry, updateList, deleteList}
}
